import React from "react";
import { Link, NavLink } from "react-router-dom";
import {
  AcademyCheckIcon,
  AcademyChevronIcon,
  AcademyLessonTypeIcon,
  AcademyModuleIcon,
} from "./AcademyIcons";
import { AcademySlugImage } from "../../components/icons/academyMeta";
import { displayProgramTitle, TRAINING } from "./trainingTerms";
import { signOutCandidate } from "../../utils/candidateAuth";

export function TrainingBreadcrumbs({ items, className = "" }) {
  if (!items?.length) return null;

  return (
    <nav
      className={`training-breadcrumbs${className ? ` ${className}` : ""}`}
      aria-label="Breadcrumb"
    >
      <ol className="training-breadcrumbs__list">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="training-breadcrumbs__item">
              {isLast || !item.to ? (
                <span
                  className="training-breadcrumbs__current"
                  aria-current={isLast ? "page" : undefined}
                >
                  {item.label}
                </span>
              ) : (
                <Link to={item.to} className="training-breadcrumbs__link">
                  {item.label}
                </Link>
              )}
              {!isLast && (
                <span className="training-breadcrumbs__sep" aria-hidden="true">
                  /
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function parseProgramHighlights(academy) {
  const raw = academy?.highlights_json;
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function programSummaryText(academy, maxLength = 200) {
  const text = academy?.purpose || academy?.description || "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
}

export function TrainingProgramOverview({ academy }) {
  const highlights = parseProgramHighlights(academy);
  if (!academy?.description && !highlights) return null;

  const stats = highlights
    ? [
        highlights.audience && { label: "Who it is for", value: highlights.audience },
        highlights.time_commitment && { label: "Time per module", value: highlights.time_commitment },
        highlights.modules_available != null && {
          label: "Modules live",
          value:
            highlights.modules_planned != null
              ? `${highlights.modules_available} of ${highlights.modules_planned} planned`
              : String(highlights.modules_available),
        },
        highlights.cert_levels > 0 && {
          label: "Certification levels",
          value: String(highlights.cert_levels),
        },
        highlights.cost && { label: "Cost", value: highlights.cost },
        highlights.format && { label: "Format", value: highlights.format },
      ].filter(Boolean)
    : [];

  return (
    <div className="training-program-overview">
      {stats.length > 0 && (
        <dl className="training-program-stats">
          {stats.map((item) => (
            <div key={item.label} className="training-program-stat">
              <dt>{item.label}</dt>
              <dd>{item.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {academy.description && (
        <div className="training-program-description">
          {academy.description.split("\n\n").map((paragraph) => (
            <p key={paragraph.slice(0, 48)}>{paragraph}</p>
          ))}
        </div>
      )}

      {highlights?.typical_roles?.length > 0 && (
        <div className="training-program-roles">
          <h4 className="training-program-roles__title">Typical roles</h4>
          <ul className="training-program-roles__list">
            {highlights.typical_roles.map((role) => (
              <li key={role}>{role}</li>
            ))}
          </ul>
        </div>
      )}

      {highlights?.outcomes?.length > 0 && (
        <div className="training-program-outcomes">
          <h4 className="training-program-outcomes__title">What you will learn</h4>
          <ul className="training-program-outcomes__list">
            {highlights.outcomes.map((outcome) => (
              <li key={outcome}>{outcome}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function lessonTypeLabel(type) {
  const labels = {
    video: "Watch video",
    text: "Read",
    download: "Download",
    image: "View image",
    quiz: "Quiz",
  };
  return labels[type] || type;
}

export function formatLessonDuration(seconds) {
  if (!seconds || seconds <= 0) return null;
  const mins = Math.ceil(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const remainder = mins % 60;
  return remainder ? `${hours} hr ${remainder} min` : `${hours} hr`;
}

export function sumLessonDurations(lessons = []) {
  return lessons.reduce((total, lesson) => total + (lesson.duration_sec || 0), 0);
}

export function countAcademyModules(academies = []) {
  let total = 0;
  let done = 0;
  for (const academy of academies) {
    for (const mod of academy.modules || []) {
      total += 1;
      if (mod.status === "completed") done += 1;
    }
  }
  return { total, done };
}

export function countMandatoryPending(academies = []) {
  let pending = 0;
  for (const academy of academies) {
    for (const mod of academy.modules || []) {
      if (mod.is_mandatory && mod.status !== "completed") pending += 1;
    }
  }
  return pending;
}

export function moduleProgressSummary(mod) {
  if (mod.status === "completed") {
    const quiz =
      mod.quiz_best_score != null
        ? mod.quiz_best_score === 100
          ? "Quiz 100%"
          : `Quiz ${mod.quiz_best_score}%`
        : "Quiz passed";
    return { badge: "Complete", badgeClass: "complete", detail: `All lessons done · ${quiz}` };
  }
  if (mod.status === "in_progress") {
    const parts = [];
    if (mod.watch_pct > 0) parts.push(`${mod.watch_pct}% lessons`);
    if (mod.quiz_best_score != null) parts.push(`Quiz ${mod.quiz_best_score}%`);
    return {
      badge: "In progress",
      badgeClass: "progress",
      detail: parts.length ? parts.join(" · ") : "Started",
    };
  }
  return { badge: "Not started", badgeClass: "idle", detail: mod.is_mandatory ? "Required module" : "Open to begin" };
}

export function moduleProgressPct(mod) {
  if (mod.status === "completed") return 100;
  return Math.min(100, mod.watch_pct || 0);
}

export function TrainingProgressByProgram({ academies, baseUrl = "/academy", issuedCertCounts = null }) {
  if (!academies?.length) {
    return (
      <div className="training-empty">
        <p>No {TRAINING.programs.toLowerCase()} available yet.</p>
      </div>
    );
  }

  return (
    <div className="training-progress-programs">
      {academies.map((academy) => {
        const modules = academy.modules || [];
        const { total, done } = countAcademyModules([academy]);
        const pct = total ? Math.round((done / total) * 100) : 0;
        const programTitle = displayProgramTitle(academy.title);
        const certsEarned = issuedCertCounts?.get?.(academy.slug) || 0;

        return (
          <section key={academy.slug} className="training-progress-program">
            <header className="training-progress-program__head">
              <div>
                <h3 className="training-progress-program__title">{programTitle}</h3>
                <p className="training-progress-program__summary">
                  {done} of {total} {TRAINING.modules.toLowerCase()} complete
                  {certsEarned > 0 &&
                    ` · ${certsEarned} certificate${certsEarned === 1 ? "" : "s"} earned`}
                </p>
              </div>
              <Link to={`${baseUrl}/${academy.slug}`} className="training-progress-program__link">
                View {TRAINING.program.toLowerCase()}
              </Link>
            </header>
            <TrainingProgressBar value={pct} label={`${programTitle} progress`} />
            <ul className="training-progress-modules">
              {modules.map((mod) => {
                const summary = moduleProgressSummary(mod);
                const barPct = moduleProgressPct(mod);
                return (
                  <li key={`${academy.slug}-${mod.slug}`}>
                    <Link
                      to={`${baseUrl}/${academy.slug}/module/${mod.slug}`}
                      className="training-progress-module"
                    >
                      <div className="training-progress-module__main">
                        <strong>{mod.title}</strong>
                        <span className="training-progress-module__detail">{summary.detail}</span>
                      </div>
                      <span className={`training-progress-module__badge training-progress-module__badge--${summary.badgeClass}`}>
                        {summary.badge}
                      </span>
                      <div className="training-progress-module__bar" aria-hidden="true">
                        <div className="training-progress-fill" style={{ width: `${barPct}%` }} />
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

export function findNextModule(academies = []) {
  for (const academy of academies) {
    for (const mod of academy.modules || []) {
      if (mod.status !== "completed") {
        return { academy, mod };
      }
    }
  }
  return null;
}

export function TrainingProgressBar({ value, label }) {
  return (
    <div className="training-progress-block">
      {label && (
        <div className="training-progress-label">
          <span>{label}</span>
          <span>{value}%</span>
        </div>
      )}
      <div className="training-progress-bar" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100}>
        <div className="training-progress-fill" style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

export function getLessonProgress(lessons = []) {
  const total = lessons.length;
  const done = lessons.filter((l) => l.status === "completed").length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return { total, done, pct };
}

export function TrainingSteps({ steps, current = 0, completed = null }) {
  if (!steps?.length) return null;
  const hasCompleted = Array.isArray(completed) && completed.length === steps.length;
  return (
    <ol className="training-steps">
      {steps.map((step, i) => {
        const isDone = hasCompleted ? completed[i] : i < current;
        const isCurrent = i === current;
        return (
          <li
            key={`${step}-${i}`}
            className={`training-step ${isDone ? "done" : ""} ${isCurrent ? "current" : ""}`}
          >
            <span className="training-step-dot">
              {isDone ? <AcademyCheckIcon size={14} /> : i + 1}
            </span>
            <span className="training-step-text">{step}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function TrainingTypeChip({ type, className = "" }) {
  const label =
    type === "program"
      ? TRAINING.program
      : type === "module"
        ? TRAINING.module
        : type === "lesson"
          ? TRAINING.lesson
          : type;
  if (!label) return null;
  return (
    <span className={`training-type-chip training-type-chip--${type} ${className}`.trim()}>{label}</span>
  );
}

export function TrainingTitleRow({ typeChip, title, as: Tag = "h2", className = "", titleClassName = "training-title" }) {
  return (
    <div className={`training-title-row ${className}`.trim()}>
      {typeChip && <TrainingTypeChip type={typeChip} />}
      <Tag className={`training-title-row__text ${titleClassName}`.trim()}>{title}</Tag>
    </div>
  );
}

export function TrainingHero({ eyebrow, title, subtitle, children, typeChip }) {
  return (
    <section className="training-hero">
      {eyebrow && <div className="training-kicker">{eyebrow}</div>}
      <TrainingTitleRow typeChip={typeChip} title={title} />
      {subtitle && <p className="training-subtitle">{subtitle}</p>}
      {children}
    </section>
  );
}

export function TrainingStickyActions({ children }) {
  return <div className="training-sticky-actions">{children}</div>;
}

export function TrainingFeedback({ feedback }) {
  if (!feedback) return null;
  return (
    <div
      className={`training-callout training-feedback training-feedback-${feedback.type}`}
      role="status"
    >
      <strong>{feedback.type === "success" ? "Done" : "Not yet"}</strong>
      {feedback.message}
    </div>
  );
}

export function CertAvailabilityNote({ className = "" }) {
  return (
    <aside
      className={`training-callout training-cert-availability${className ? ` ${className}` : ""}`}
    >
      <strong>Why a certificate may not appear yet</strong>
      <p className="training-cert-availability__lead">
        A certificate only shows once every requirement for that tier is complete.
      </p>
      <ul className="training-callout-list">
        <li>Some programs do not offer certificates yet.</li>
        <li>Every required lesson must be finished. Scroll or watch through each one.</li>
        <li>
          Certification usually needs a perfect quiz score (100%), not just a pass.
        </li>
        <li>Enough active time on lessons is required. Opening a page is not enough.</li>
        <li>Higher certificates unlock after the previous tier is issued.</li>
      </ul>
    </aside>
  );
}

export function CertBlockersList({
  items,
  title = "Certificate not ready yet",
  className = "",
  hideTierTitles = false,
}) {
  if (!items?.length) return null;
  return (
    <aside className={`training-callout training-cert-blockers${className ? ` ${className}` : ""}`}>
      <strong>{title}</strong>
      {items.map((item) => (
        <div key={item.cert_slug} className="training-cert-blockers__tier">
          {!hideTierTitles && item.cert_title && (
            <p className="training-cert-blockers__tier-title">{item.cert_title}</p>
          )}
          {item.blockers?.length > 0 && (
            <ul className="training-callout-list">
              {item.blockers.map((reason, i) => (
                <li key={`${item.cert_slug}-${i}`}>{reason}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </aside>
  );
}

export function certDetailPath(programSlug, certSlug) {
  return `/me/certificates/${encodeURIComponent(programSlug)}/${encodeURIComponent(certSlug)}`;
}

function formatCompetenceLevel(level) {
  if (!level) return null;
  return `${level.charAt(0).toUpperCase()}${level.slice(1)} level`;
}

function moduleProgressLabel(mod) {
  if (mod.module_complete) return "Complete";
  if (mod.quiz_best_score != null && mod.quiz_best_score < 100) {
    return `Quiz ${mod.quiz_best_score}%`;
  }
  if (mod.lessons_total > 0) {
    return `${mod.lessons_done || 0}/${mod.lessons_total} lessons`;
  }
  return "Not started";
}

export function CertRequiredModulesList({ modules, showProgress = false, compact = false }) {
  if (!modules?.length) return null;
  return (
    <ul className={`training-cert-modules${compact ? " training-cert-modules--compact" : ""}`}>
      {modules.map((mod) => (
        <li key={mod.slug} className="training-cert-modules__item">
          <Link to={mod.href} className="training-cert-modules__link">
            {mod.title}
          </Link>
          {showProgress && (
            <span
              className={`training-cert-modules__status${
                mod.module_complete ? " training-cert-modules__status--done" : ""
              }`}
            >
              {moduleProgressLabel(mod)}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

export function CertRequirementSummary({ cert }) {
  const parts = [];
  if (cert.prerequisite_title) {
    parts.push(`Requires ${cert.prerequisite_title} first.`);
  }
  if (cert.require_perfect_quiz !== false) {
    parts.push("100% on every required module quiz.");
  }
  if (cert.min_time_spent_sec) {
    const mins = Math.ceil(cert.min_time_spent_sec / 60);
    parts.push(`About ${mins} minutes of active learning across required modules.`);
  }
  if (!parts.length) return null;
  return <p className="training-cert-requirements-summary">{parts.join(" ")}</p>;
}

export function groupCertificationsByProgram(certifications) {
  const groups = new Map();
  for (const cert of certifications || []) {
    const slug = cert.academy_slug || "unknown";
    if (!groups.has(slug)) {
      groups.set(slug, {
        programSlug: slug,
        programTitle: displayProgramTitle(cert.academy_title || slug),
        programSortOrder: cert.academy_sort_order ?? 999,
        certifications: [],
      });
    }
    groups.get(slug).certifications.push(cert);
  }
  return [...groups.values()]
    .sort((a, b) => a.programSortOrder - b.programSortOrder)
    .map((group) => {
      const issuedCount = group.certifications.filter((c) => c.cert_status === "issued").length;
      return {
        ...group,
        issuedCount,
        tierCount: group.certifications.length,
        certifications: group.certifications.sort(
          (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
        ),
      };
    });
}

export function groupIssuedCertificatesByProgram(certificates) {
  const groups = new Map();
  for (const cert of certificates || []) {
    if (cert.status !== "issued") continue;
    const slug = cert.academy_slug || "unknown";
    if (!groups.has(slug)) {
      groups.set(slug, {
        programSlug: slug,
        programTitle: displayProgramTitle(cert.academy_title || slug),
        certificates: [],
      });
    }
    groups.get(slug).certificates.push(cert);
  }
  return [...groups.values()].sort((a, b) => a.programTitle.localeCompare(b.programTitle));
}

export function countIssuedCertsByProgram(items, { statusField = "status" } = {}) {
  const counts = new Map();
  for (const item of items || []) {
    const status = item[statusField] ?? item.cert_status ?? item.status;
    if (status !== "issued") continue;
    const slug = item.academy_slug || "unknown";
    counts.set(slug, (counts.get(slug) || 0) + 1);
  }
  return counts;
}

export function CertStatusBadge({ status }) {
  const labels = {
    issued: "Issued",
    eligible: "Ready to claim",
    in_progress: "In progress",
    locked: "Locked",
  };
  return (
    <span className={`training-cert-badge training-cert-badge--${status || "locked"}`}>
      {labels[status] || status}
    </span>
  );
}

export function CertDownloadButton({
  loading = false,
  onClick,
  className = "training-btn-primary",
  label = "Download PDF",
  loadingLabel = "Preparing...",
}) {
  return (
    <button
      type="button"
      className={className}
      disabled={loading}
      aria-busy={loading}
      onClick={onClick}
    >
      {loading && <span className="training-btn-spinner" aria-hidden="true" />}
      {loading ? loadingLabel : label}
    </button>
  );
}

export function TrainingCertLadder({
  certifications,
  showProgramHeaders = false,
  onDownload,
  extractingId,
  showBlockers = false,
  issuedLink,
  showRequiredModules = true,
  showDetailLinks = true,
}) {
  if (!certifications?.length) {
    return <div className="training-empty">No certifications configured yet.</div>;
  }

  const groups = showProgramHeaders
    ? groupCertificationsByProgram(certifications)
    : [{ programSlug: "all", programTitle: null, certifications }];

  return (
    <>
      {groups.map((group) => (
        <div key={group.programSlug} className="training-cert-program-group">
          {group.programTitle && (
            <>
              <h4 className="training-cert-program-group__title">{group.programTitle}</h4>
              {group.tierCount > 0 && (
                <p className="training-cert-program-group__meta">
                  {group.issuedCount} of {group.tierCount} certificate
                  {group.tierCount === 1 ? "" : "s"} earned
                </p>
              )}
            </>
          )}
          <ol className="training-cert-ladder">
            {group.certifications.map((c, i) => (
              <li key={c.slug} className={`training-cert-step training-cert-step--${c.cert_status || "locked"}`}>
                <span className="training-cert-step__num">{i + 1}</span>
                <div className="training-cert-step__body">
                  <div className="training-cert-step__title-row">
                    {showDetailLinks && group.programSlug !== "all" ? (
                      <Link to={certDetailPath(group.programSlug, c.slug)} className="training-cert-step__title-link">
                        <strong>{c.title}</strong>
                      </Link>
                    ) : (
                      <strong>{c.title}</strong>
                    )}
                    <CertStatusBadge status={c.cert_status} />
                  </div>
                  {c.competence_level && (
                    <p className="training-cert-step__level">{formatCompetenceLevel(c.competence_level)}</p>
                  )}
                  {c.description && <p>{c.description}</p>}
                  {showRequiredModules && c.required_modules?.length > 0 && (
                    <div className="training-cert-step__modules">
                      <span className="training-cert-step__modules-label">Required {TRAINING.modules.toLowerCase()}</span>
                      <CertRequiredModulesList
                        modules={c.required_modules}
                        showProgress={c.cert_status !== "locked"}
                        compact
                      />
                    </div>
                  )}
                  <CertRequirementSummary cert={c} />
                  {c.cert_status === "issued" && onDownload && c.certificate_id && (
                    <CertDownloadButton
                      className="training-btn-secondary training-cert-step__btn"
                      loading={extractingId === c.certificate_id}
                      onClick={() => onDownload(c.certificate_id)}
                    />
                  )}
                  {issuedLink && c.cert_status === "issued" && (
                    <Link to={issuedLink} className="training-cert-step__link">
                      Download certificate
                    </Link>
                  )}
                  {showDetailLinks && group.programSlug !== "all" && (
                    <Link to={certDetailPath(group.programSlug, c.slug)} className="training-cert-step__link">
                      Certificate details
                    </Link>
                  )}
                  {showBlockers &&
                    c.cert_status === "in_progress" &&
                    c.cert_progress?.blockers?.length > 0 && (
                    <CertBlockersList
                      items={[{ cert_slug: c.slug, blockers: c.cert_progress.blockers }]}
                      title="Still needed"
                      className="training-cert-blockers--inline"
                      hideTierTitles
                    />
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </>
  );
}

export function TrainingAccountSubnav({ ariaLabel = "Training sections" }) {
  const linkClass = ({ isActive }) => `training-nav-link${isActive ? " is-active" : ""}`;

  return (
    <nav className="training-subnav" aria-label={ariaLabel}>
      <NavLink to="/academy" end className={linkClass}>
        Programs
      </NavLink>
      <NavLink to="/me" end className={linkClass}>
        Progress
      </NavLink>
      <NavLink to="/me/profile" className={linkClass}>
        Profile
      </NavLink>
      <NavLink to="/me/certificates" className={linkClass}>
        <span className="training-nav-link__short" aria-hidden="true">
          Certs
        </span>
        <span className="training-nav-link__long">Certificates</span>
      </NavLink>
      <NavLink to="/me/applications" className={linkClass}>
        <span className="training-nav-link__short" aria-hidden="true">
          Jobs
        </span>
        <span className="training-nav-link__long">Applications</span>
      </NavLink>
    </nav>
  );
}

export function TrainingSignOutSection({ className = "" }) {
  return (
    <section className={`training-signout${className ? ` ${className}` : ""}`}>
      <button
        type="button"
        className="training-btn-secondary training-signout__btn"
        onClick={() => signOutCandidate({ redirectTo: "/login" })}
      >
        Sign out
      </button>
    </section>
  );
}

export function TrainingLoading({ label = "Loading..." }) {
  return (
    <div className="training-loading" aria-busy="true">
      <div className="training-loading__spinner" aria-hidden="true" />
      {label}
    </div>
  );
}

export function TrainingError({ title = "Could not load", message, action }) {
  return (
    <div className="training-empty training-error">
      <p><strong>{title}</strong></p>
      {message && <p>{message}</p>}
      {action}
    </div>
  );
}

export function TrainingSignInBanner({ nextPath = "/academy" }) {
  return (
    <div className="training-signin-banner">
      <div>
        <strong>Sign in to track progress</strong>
        <p>Save your place, earn certificates, and show recruiters what you&apos;ve completed.</p>
      </div>
      <Link to={`/login?next=${encodeURIComponent(nextPath)}`} className="training-btn-primary training-signin-banner__btn">
        Sign in
      </Link>
    </div>
  );
}

export function TrainingAcademyTile({ academy, to, progressPct }) {
  return (
    <Link to={to} className={`training-academy-tile training-academy-tile--${academy.slug}`}>
      <div className="training-academy-tile__media">
        <AcademySlugImage slug={academy.slug} className="training-academy-tile__photo" />
      </div>
      <div className="training-academy-tile__body">
        {academy.kicker && <span className="training-academy-tile__kicker">{academy.kicker}</span>}
        <h3 className="training-academy-tile__title">
          <TrainingTypeChip type="program" />
          <span>{displayProgramTitle(academy.title)}</span>
        </h3>
        <p className="training-academy-tile__purpose">
          {programSummaryText(academy, 200)}
        </p>
        {typeof progressPct === "number" && progressPct > 0 && (
          <div className="training-academy-tile__progress">
            <div className="training-progress-bar">
              <div className="training-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
            <span>{progressPct}% complete</span>
          </div>
        )}
      </div>
      <div className="training-academy-tile__footer">
        <span>Explore {TRAINING.program.toLowerCase()}</span>
        <AcademyChevronIcon />
      </div>
    </Link>
  );
}

export function TrainingModuleCard({ mod, to, isCurrent = false }) {
  const isDone = mod.status === "completed";
  return (
    <Link
      to={to}
      className={`training-module-card ${isDone ? "is-done" : ""} ${isCurrent ? "is-current" : ""}`}
    >
      <div className="training-module-icon">
        <AcademyModuleIcon name={mod.icon} />
      </div>
      <div className="training-module-content">
        <div className="training-module-title">
          <TrainingTypeChip type="module" />
          <span className="training-module-title__text">{mod.title}</span>
          {mod.is_mandatory && <span className="training-badge-mandatory">Required</span>}
        </div>
        <div className="training-module-meta">
          {isDone ? (
            <span className="training-badge-done">
              <AcademyCheckIcon size={13} /> Completed
            </span>
          ) : mod.watch_pct > 0 ? (
            <span className="training-badge-progress">
              {mod.watch_pct}% lessons
              {mod.quiz_best_score != null ? ` · Quiz ${mod.quiz_best_score}%` : ""}
            </span>
          ) : mod.competence_level ? (
            <span className="training-badge-level">{mod.competence_level} level</span>
          ) : (
            "Start module"
          )}
        </div>
      </div>
      <div className="training-module-chevron">
        <AcademyChevronIcon />
      </div>
    </Link>
  );
}

export function TrainingLessonList({ lessons, onSelect, nextLessonId }) {
  return (
    <div className="training-lesson-list">
      {lessons.map((lesson, i) => {
        const isDone = lesson.status === "completed";
        const isNext = lesson.id === nextLessonId;
        const durationLabel = formatLessonDuration(lesson.duration_sec);
        return (
          <button
            key={lesson.id}
            type="button"
            className={`training-lesson-item ${isDone ? "done" : ""} ${isNext ? "next-up" : ""}`}
            onClick={() => onSelect(lesson)}
          >
            <span className={`training-lesson-num ${isDone ? "done" : ""}`}>
              {isDone ? <AcademyCheckIcon size={14} /> : i + 1}
            </span>
            <span className="training-lesson-body">
              <strong>{lesson.title}</strong>
              <span className="training-lesson-type">
                <AcademyLessonTypeIcon type={lesson.lesson_type} />
                <span className="training-lesson-type__label">
                  {lessonTypeLabel(lesson.lesson_type)}
                </span>
                {durationLabel && (
                  <>
                    <span className="training-lesson-type__sep" aria-hidden="true">
                      ·
                    </span>
                    <span className="training-lesson-duration">{durationLabel}</span>
                  </>
                )}
              </span>
            </span>
            <span className="training-lesson-action">{isDone ? "Review" : isNext ? "Continue" : "Open"}</span>
          </button>
        );
      })}
    </div>
  );
}

export function scrollTrainingToTop(opts = {}) {
  const behavior = opts.instant ? "auto" : "smooth";
  window.scrollTo({ top: 0, left: 0, behavior });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  const trainingBody = document.querySelector(".training-body");
  if (trainingBody) {
    trainingBody.scrollTop = 0;
    trainingBody.scrollTo?.({ top: 0, left: 0, behavior });
  }
  document.querySelector(".training-shell")?.scrollTo?.({ top: 0, left: 0, behavior });
}

export const OPTION_LETTERS = ["A", "B", "C", "D", "E", "F"];
